"use client";

import React from "react";
import { ArrowRight, ArrowDown, TrendingUp } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

/**
 * BrokerProgram — Section 2: Kommissionsmodell (Money-Math)
 * Drop-in für: src/pages/BrokerProgram/sections/Pricing.tsx
 *
 * Der Killer-Hook für Vermittler: konkrete Zahlen pro Empfehlung +
 * Hochrechnung für realistisches passives Einkommen.
 */

const tiers = [{
  product: "Starter",
  price: "CHF 24/Mo",
  commission: "CHF 86.40",
  note: "30 % × 12 Monate"
}, {
  product: "Pro Website",
  price: "CHF 72/Mo",
  commission: "CHF 259.20",
  note: "30 % × 12 Monate",
  featured: true
}, {
  product: "Pro Webshop",
  price: "CHF 144/Mo",
  commission: "CHF 518.40",
  note: "30 % × 12 Monate"
}];
export default function Pricing() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  return <section id="kommissionsmodell" className="bg-secondary py-20 md:py-24">
      <div ref={ref} className={`max-w-6xl mx-auto px-4 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-4 leading-tight">
            Ihre Provision pro Empfehlung
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground">
            30 % Provision auf die ersten 12 Monate — schwarz auf weiss.
          </p>
        </div>

        {/* 3 Provisions-Karten */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {tiers.map((t, i) => <div key={i} className={`relative rounded-2xl p-6 md:p-7 ${t.featured ? "bg-primary text-white shadow-xl shadow-primary/30 md:scale-105" : "bg-background border border-border"}`}>
              {t.featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-foreground text-background text-xs font-bold px-3 py-1 rounded-md shadow-md">
                  Beliebtester Tarif
                </span>}
              <div className={`text-[10px] font-semibold tracking-wider uppercase mb-2 ${t.featured ? "text-white/80" : "text-muted-foreground"}`}>
                {t.product} · {t.price}
              </div>
              <div className="text-3xl md:text-4xl font-bold mb-1 leading-none">
                {t.commission}
              </div>
              <div className={`text-sm mt-2 ${t.featured ? "text-white/80" : "text-muted-foreground"}`}>
                pro Empfehlung
              </div>
              <div className={`text-xs mt-1 ${t.featured ? "text-white/60" : "text-muted-foreground/70"}`}>
                {t.note}
              </div>
            </div>)}
        </div>

        {/* Hochrechnungs-Box */}
        <div className="relative bg-gradient-to-br from-primary/5 via-primary/[0.03] to-transparent border border-primary/20 rounded-2xl md:rounded-3xl p-6 md:p-10 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle, var(--color-foreground) 1px, transparent 1px)",
          backgroundSize: "20px 20px"
        }} />
          <div className="relative">
            <div className="text-center mb-8 md:mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-3">
                <TrendingUp className="w-3 h-3" />
                Beispielrechnung
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                Ihr passives Einkommen
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-4 md:gap-2 items-stretch">
              <div className="bg-background border border-border rounded-xl p-5 md:p-6 text-center">
                <div className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase mb-2">
                  Sie empfehlen
                </div>
                <div className="text-3xl md:text-4xl font-bold text-foreground mb-1 leading-none">
                  10 Kunden
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  pro Jahr · Pro Website-Tarif
                </div>
              </div>

              <div className="flex items-center justify-center text-muted-foreground/40 py-2 md:py-0">
                <ArrowRight className="w-5 h-5 hidden md:block" />
                <ArrowDown className="w-5 h-5 md:hidden" />
              </div>

              <div className="bg-background border border-border rounded-xl p-5 md:p-6 text-center">
                <div className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase mb-2">
                  Provision pro Kunde
                </div>
                <div className="text-3xl md:text-4xl font-bold text-foreground mb-1 leading-none">
                  CHF 259
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  über 12 Monate verteilt
                </div>
              </div>

              <div className="flex items-center justify-center text-muted-foreground/40 py-2 md:py-0">
                <ArrowRight className="w-5 h-5 hidden md:block" />
                <ArrowDown className="w-5 h-5 md:hidden" />
              </div>

              <div className="bg-primary text-white rounded-xl p-5 md:p-6 text-center shadow-lg shadow-primary/20">
                <div className="text-[10px] font-semibold tracking-wider text-white/80 uppercase mb-2">
                  Ihr Verdienst
                </div>
                <div className="text-3xl md:text-4xl font-bold mb-1 leading-none">
                  CHF 2'592
                </div>
                <div className="text-xs text-white/80 mt-2">
                  passiv · pro Jahr
                </div>
              </div>
            </div>

            <p className="text-center text-xs text-muted-foreground mt-6 md:mt-8">
              Bei 30 Empfehlungen pro Jahr: CHF 7'776 · Bei 50: CHF 12'960. Kein
              Limit nach oben.
            </p>
          </div>
        </div>
      </div>
    </section>;
}